import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Tabs, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { invoiceAPI } from '../services/api';

const InvoiceManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await invoiceAPI.getAll();
      setInvoices(response.data);
    } catch (error) {
      message.error('Lỗi khi tải danh sách invoice');
    }
    setLoading(false);
  };

  const handleSubmit = async (values) => {
    try {
      if (editingInvoice) {
        await invoiceAPI.update(editingInvoice._id, values);
        message.success('Cập nhật invoice thành công');
      } else {
        await invoiceAPI.create(values);
        message.success('Tạo invoice thành công');
      }
      setModalVisible(false);
      setEditingInvoice(null);
      form.resetFields();
      fetchInvoices();
    } catch (error) {
      message.error(editingInvoice ? 'Lỗi khi cập nhật invoice' : 'Lỗi khi tạo invoice');
    }
  };

  const handleEdit = (record) => {
    setEditingInvoice(record);
    form.setFieldsValue({
      code: record.code,
      rateAppCtv: record.rateAppCtv,
      rateVpcsCtv: record.rateVpcsCtv,
      rateAppMain: record.rateAppMain,
      rateVpcsMain: record.rateVpcsMain
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await invoiceAPI.delete(id);
      message.success('Xóa invoice thành công');
      fetchInvoices();
    } catch (error) {
      message.error('Lỗi khi xóa invoice');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await invoiceAPI.toggleStatus(id);
      message.success(currentStatus ? 'Đã khóa Invoice' : 'Đã mở khóa Invoice');
      fetchInvoices();
    } catch (error) {
      message.error('Lỗi khi thay đổi trạng thái Invoice');
    }
  };

  const columns = [
    { title: 'Tên Invoice', dataIndex: 'code', key: 'code' },
    { title: 'Rate APP CTV (%)', dataIndex: 'rateAppCtv', key: 'rateAppCtv' },
    { title: 'Rate VPCS CTV (%)', dataIndex: 'rateVpcsCtv', key: 'rateVpcsCtv' },
    { title: 'Rate APP Chủ (%)', dataIndex: 'rateAppMain', key: 'rateAppMain' },
    { title: 'Rate VPCS Chủ (%)', dataIndex: 'rateVpcsMain', key: 'rateVpcsMain' },
    { 
      title: 'Tổng tiêu thụ', 
      dataIndex: 'totalSpentMain', 
      key: 'totalSpentMain', 
      render: (val) => `$${val?.toLocaleString()}` 
    },
    { 
      title: 'Lợi nhuận', 
      dataIndex: 'totalProfit', 
      key: 'totalProfit', 
      render: (val) => `$${val?.toLocaleString()}` 
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (val) => (
        <span style={{ color: val ? 'green' : 'red', fontWeight: 'bold' }}>
          {val ? 'Đang hoạt động' : 'Đã khóa'}
        </span>
      )
    },
    {
      title: 'Hành động',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" href={`/invoices/${record._id}`}>Chi tiết</Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Sửa</Button>
          <Popconfirm
            title={record.isActive ? 'Xác nhận khóa Invoice?' : 'Xác nhận mở khóa Invoice?'}
            onConfirm={() => handleToggleStatus(record._id, record.isActive)}
          >
            <Button type="link" style={{ color: record.isActive ? 'orange' : 'green' }}>
              {record.isActive ? 'Khóa' : 'Mở khóa'}
            </Button>
          </Popconfirm>
          <Popconfirm title="Xác nhận xóa?" onConfirm={() => handleDelete(record._id)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>Quản lý Invoice</h1>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} style={{ marginBottom: 16 }}>
        Tạo Invoice mới
      </Button>
      
      <Table columns={columns} dataSource={invoices} loading={loading} rowKey="_id" />

      <Modal
        title={editingInvoice ? 'Chỉnh sửa Invoice' : 'Tạo Invoice mới'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingInvoice(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item name="code" label="Mã Invoice" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="rateAppCtv" label="Rate APP CTV (%)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="rateVpcsCtv" label="Rate VPCS CTV (%)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="rateAppMain" label="Rate APP Chủ (%)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="rateVpcsMain" label="Rate VPCS Chủ (%)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            {editingInvoice ? 'Cập nhật' : 'Tạo'}
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default InvoiceManagement;
